/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useEffect, useRef, createRef, useState, useMemo } from 'react';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material'
import { ChatMessage } from './ChatMessage';
import { SelectPersona } from './Components/SelectPersona.js';
import { SelectDataset } from './Components/SelectDataset.js';
import { ChatInput } from './ChatInput.js';
import { ChatInputNew } from './SimpleChatInput/ChatInputNew.js';
import Select from 'react-select';

function processDataset(inputString) {
  // Initialize a counter for underscores

  inputString = inputString.replace('user_custom_', '');

  let underscoreCount = 0;

  // Iterate through the input string
  for (let i = 0; i < inputString.length; i++) {
    // If an underscore is found, increment the counter
    if (inputString[i] === '_') {
      underscoreCount++;

      // If the counter reaches 2, return the substring up to the 2nd underscore
      if (underscoreCount === 2) {
        return inputString.substring(inputString.indexOf("_") + 1).replace('_content', '');
      }
    }
  }
  // If there are less than or equal to 2 underscores, return the original string

  if (/^\d+$/.test(inputString.replace('_content', ''))) {
    return "User " + inputString.replace('_content', '');
  }   
  return inputString;
}

const SelectSingleDatasetNew = ({ value, datasets, datasetPermissions, onChange, noAllNone, default_data }) => {
  const options = [];
  if (noAllNone !== null && noAllNone === 'true') {
  } else {
    options.push(
      { value: 'all', label: 'All' },
      { value: 'none', label: 'None' }
    );
  }

  datasets.forEach(function (obj) {
    var label = processDataset(obj);
    if (noAllNone !== null && noAllNone === 'true') {
      if (obj.indexOf('user_custom_') === -1) {
        return;
      }
    }
    if (datasetPermissions && default_data.default_hide_cac === false) {
      for (let i = 0; i < datasetPermissions.length; i++) {
        const cur_dataset = datasetPermissions[i];
        if (obj === cur_dataset.dataset) {
          if(cur_dataset.classification === 'Unclassified') {
            label += ' (U)';
          }
          else {
            label += ' (' + cur_dataset.classification + ')';
          }
          break;
        }
      }
    }

    options.push(
      { value: obj, label: label }
    );
  });

  const handleChange = (
    newValue,
    actionMeta
  ) => {
    switch (actionMeta.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionMeta.removedValue.isFixed) {
          return;
        }
        break;
      case 'clear':
        newValue = options.filter((v) => v.isFixed);
        break;
      default:
    }
    if(newValue === undefined || newValue.length === 0) {
      if (noAllNone !== null && noAllNone === 'true') {
        newValue = ''
        onChange(newValue);
        return;
      }
      else {
        newValue = options[0]
      }
    }

    onChange(newValue);
  };

  const styles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: '#eeeeee',
      borderColor: 'black',
      fontSize: '10pt',
   }),
   valueContainer: (base) => ({
    ...base,
    maxHeight: 40,
    minWidth:200,
    overflowY: "auto",
    fontSize: '10pt'
  }),
    menuPortal: base => ({
      ...base,
      fontSize: '10pt'
    }),
    menu: (provided, state) => ({
      ...provided,
      backgroundColor: '#eee',
      fontSize: '10pt'
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? '#eee'
        : isFocused
        ? '#444654'
        : undefined,
        color: isDisabled
          ? '#ccc'
          : isSelected
          ? 'grey'
          : isFocused
          ? 'white'
          : 'black',
          cursor: isDisabled ? 'not-allowed' : 'default',
        ':active': {
          ...styles[':active'],
          backgroundColor: !isDisabled
            ? isSelected
              ? 'purple'
              : undefined
            : undefined,
        },
        fontSize: '9pt',
        padding: '3px 10px',
      };
    },
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: '#444654',
        fontSize: '10pt',
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: 'white',
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: 'white',
    }),
  };

  return (
    <Select
      name="dataset"
      options={options}
      value={value}
      onChange={handleChange}
      styles={styles}
      maxMenuHeight={160}
      menuPlacement="auto"
    />
  );
};

const SelectSingleSecret = ({value, secrets, onChange}) => {    
  const options = [];
  options.push(      
    <option key={"undefined"} value={"undefined"}>
      Select a Secret to connect
    </option>
  );

  secrets.forEach(function(obj) {
    var label = obj;
    options.push(      
      <option key={obj} value={obj}>
        {label}
      </option>
    );
  });

  return (
    <select name="secret" value={value} onChange={onChange} className="select-menu">
      {options}
    </select>
  );
};

const SelectFileStrategy = ({onChange}) => {    
  const options = [];
  options.push(      
    <option key="auto" value="auto">
      Auto
    </option>
  );
  options.push(      
    <option key="fast" value="fast">
      Fast
    </option>
  );
  options.push(      
    <option key="hi_res" value="hi_res">
      OCR / Hi-Res
    </option>
  );
  return (
    <select name="file_strategy" onChange={onChange} className="select-menu">
      {options}
    </select>
  );
};

const SelectAudioVoice = ({onChange}) => {    
  var options = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];

  // turn options into array of <option> elements
  options = options.map((option) => {
    return (
      <option key={option} value={option}>
        {option}
      </option>
    );
  });

  return (
    <select name="audio_voice" onChange={onChange} className="select-menu">
      {options}
    </select>
  );
};

const SelectAudioModel = ({onChange}) => {    
  var options = ['tts-hd', 'tts'];

  // turn options into array of <option> elements
  options = options.map((option) => {
    return (
      <option key={option} value={option}>
        {option}
      </option>
    );
  });

  return (
    <select name="audio_model" onChange={onChange} className="select-menu">
      {options}
    </select>
  );
};

// Primary Chat Window
const ChatBox = ({
    default_data,
    chatLog, 
    currentModel, 
    currentMode,
    chatMode,
    handleChatMode, 
    cur_user, 
    handleAudio, 
    showCopyData, 
    showPersonaList, 
    showSimpleIngest, 
    handleSubmit, 
    clickHandler, 
    handleSelect, 
    handleModel, 
    temperature, 
    live, 
    handleLive, 
    showAddDataset,
    personas, 
    handlePersona, 
    current_persona, 
    clickHandlerDelete, 
    clickHandlerFollowUp, 
    clickHandlerCopy, 
    clickHandlerDown, 
    paid, 
    showPluginList, 
    datasets, 
    datasetPermissions,
    current_dataset, 
    handleDataset, 
    tokens_count, 
    clickHandlerEdit, 
    handleFileChange, 
    isCUICapable, 
    getTokenWindow,
    file,
    files, 
    setFiles, 
    getTokenizer, 
    prevCommand, 
    secrets, 
    setFile,
    setChatLogAndSaveWithScroll, 
    setCurrentPersona, 
    chatInput, 
    setChatInputMode,
    hideSidebar,
    handlePluginClick,
    setSettingDrawerOpen,
    hideModelSelect=false,
    mode='chat',
    chatbox_class="chatbox",
    maxFiles=1,
    singleFileOnly=false,
    handleChartUpdate,
    isChartRendering=false,
    isDeepAgentDrawerOpen=false, 
    deepAgentSidebarRef=null, 
    addOrUpdatFinalMessage=()=>{},
    getBotSettingItem,
    isBotLoggedIn,
    getToken,
    getUser,
    userState, 
    widthStandard='100vw',
    widthOffset=0,
    disableSidebar=false,
    disableDeepAgent=false,
    hidePluginSelect=false,
    hidePromptSelect=false
  }) => {
  const messageRefs = useRef([]);
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  messageRefs.current = chatLog.map((_, i) => messageRefs.current[i] ?? createRef());
  const isLastMeMessage = chatLog.map((message, index) => message.user === "me" ? index : -1).reduce((a, b) => Math.max(a, b), -1);
  const isLastSageMessage = chatLog.map((message, index) => message.user === "gpt" ? index : -1).reduce((a, b) => Math.max(a, b), -1);
  const [chatInputPass, setChatInputPass] = useState('');


  useEffect(() => {
    if (isLastSageMessage !== -1) {
      messageRefs.current[isLastSageMessage].current.scrollIntoView({ behavior: 'instant' });
    }
  }, [chatLog]);

  const getSize = useMemo(() => {
    if(mode === 'canvas') {
      return '400px'
    }
    else {
      if (isMobile) {
        return '100%';
      }
      const sidebarWidth = disableSidebar ? '0px' : hideSidebar ? '50px' : '260px';
      return `calc(
        ${widthStandard} - ${sidebarWidth} - ${widthOffset}px - 
        ${isDeepAgentDrawerOpen && !isMobile ? '400px' : '0px'} 
      )`
    }
  }, [hideSidebar, isDeepAgentDrawerOpen, mode, isMobile]);

  const Chatlog = useMemo(() => {
    return chatLog.map((message, index) => (
      <div ref={messageRefs.current[index]} key={`message-${index}`}>
        <ChatMessage
          clickHandlerEdit={clickHandlerEdit}
          isLastMeMessage={index === isLastMeMessage}
          isLastSageMessage={index === isLastSageMessage}
          key={index}
          index={index}
          message={message}
          clickHandlerFollowUp={clickHandlerFollowUp}
          clickHandler={clickHandler}
          clickHandlerCopy={clickHandlerCopy}
          clickHandlerDelete={clickHandlerDelete}
          clickHandlerDown={clickHandlerDown}
          setChatInput={setChatInputPass}
          deepAgentSidebarRef={deepAgentSidebarRef}
          handleChartUpdate={handleChartUpdate}
          isChartRendering={isChartRendering}
          addOrUpdatFinalMessage={addOrUpdatFinalMessage}
          default_data={default_data}
          isBotLoggedIn={isBotLoggedIn}
          userState={userState}
          getToken={getToken}
        />
      </div>
    ))
  }, [chatLog, isLastMeMessage, isLastSageMessage, clickHandlerEdit, clickHandlerFollowUp, clickHandler, clickHandlerCopy, clickHandlerDelete, clickHandler, clickHandlerDown])


  return (
    <>
      <Stack
        className={chatbox_class}
        sx={{
          height: '100%',
          minWidth: getSize,
          maxWidth: getSize,
          backgroundColor: theme.palette.chatBackground,
          transition: isDeepAgentDrawerOpen ? '.195s' : '.225s'
        }}
      >
        <Stack
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            flexDirection: 'column',
            paddingX: 1,
            paddingTop: 2,
            width: '100%',
            paddingBottom: chatInput === 'simple' ? 5 : 0,
          }}
          className={`${isBotLoggedIn() ? 'bot' : ''} chat-log`}
        >
          {Chatlog}
        </Stack>
        {userState.useModernChat ?
          <ChatInputNew
            hideModelSelect={hideModelSelect}
            mode={mode}
            getTokenizer={getTokenizer}
            handleSubmit={handleSubmit}
            prevCommand={prevCommand}
            setFiles={setFiles}
            showAddDataset={showAddDataset}
            setCurrentPersona={setCurrentPersona}
            currentMode={userState.useAdvanceFields ? 'advanced' : 'simple'}
            current_dataset={current_dataset}
            datasets={datasets}
            datasetPermissions={datasetPermissions}
            handleDataset={handleDataset}
            showSimpleIngest={showSimpleIngest}
            showCopyData={showCopyData}
            handleLive={handleLive}
            live={live}
            chatMode={chatMode}
            handleChatMode={handleChatMode}
            paid={paid}
            showPersonaList={showPersonaList}
            current_persona={current_persona}
            personas={personas}
            handlePersona={handlePersona}
            handleSelect={handleSelect}
            temperature={temperature}
            currentModel={currentModel}
            handleModel={handleModel}
            cur_user={cur_user}
            handleFileChange={handleFileChange}
            handleAudio={handleAudio}
            setChatLogAndSaveWithScroll={setChatLogAndSaveWithScroll}
            secrets={secrets}
            tokens_count={tokens_count}
            getTokenWindow={getTokenWindow}
            files={files}
            singleFile={file}
            isCUICapable={isCUICapable}
            chatInputPass={chatInputPass}
            setSingleFile={setFile}
            setChatInputPass={setChatInputPass}
            setChatInputMode={setChatInputMode}
            handlePluginClick={handlePluginClick}
            setSettingDrawerOpen={setSettingDrawerOpen}
            maxFiles={maxFiles}
            singleFileOnly={singleFileOnly}
            default_data={default_data}
            isBotLoggedIn={isBotLoggedIn}
            getBotSettingItem={getBotSettingItem}
            getToken={getToken}
            getUser={getUser}
            disableDeepAgent={disableDeepAgent}
            hidePluginSelect={hidePluginSelect}
            hidePromptSelect={hidePromptSelect}
          />
          :
          <ChatInput
            getTokenizer={getTokenizer}
            handleSubmit={handleSubmit}
            prevCommand={prevCommand}
            setFile={setFiles}
            showAddDataset={showAddDataset}
            setCurrentPersona={setCurrentPersona}
            currentMode={userState.useAdvanceFields ? 'advanced' : 'simple'}
            current_dataset={current_dataset}
            datasets={datasets}
            datasetPermissions={datasetPermissions}
            handleDataset={handleDataset}
            showSimpleIngest={showSimpleIngest}
            showCopyData={showCopyData}
            handleLive={handleLive}
            live={live}
            chatMode={chatMode}
            handleChatMode={handleChatMode}
            paid={paid}
            showPersonaList={showPersonaList}
            current_persona={current_persona}
            personas={personas}
            handlePersona={handlePersona}
            handleSelect={handleSelect}
            temperature={temperature}
            currentModel={currentModel}
            handleModel={handleModel}
            cur_user={cur_user}
            handleFileChange={handleFileChange}
            handleAudio={handleAudio}
            setChatLogAndSaveWithScroll={setChatLogAndSaveWithScroll}
            secrets={secrets}
            tokens_count={tokens_count}
            getTokenWindow={getTokenWindow}
            file={files}
            isCUICapable={isCUICapable}
            chatInputPass={chatInputPass}
            setChatInputPass={setChatInputPass}
            handlePluginClick={handlePluginClick}
            default_data={default_data}
            isBotLoggedIn={isBotLoggedIn}
            getBotSettingItem={getBotSettingItem}
            getToken={getToken}
            getUser={getUser}
            disableDeepAgent={disableDeepAgent}
          />
        }
      </Stack>
    </>
  );
}

export { ChatBox, SelectPersona, SelectDataset, SelectSingleSecret, SelectSingleDatasetNew, SelectFileStrategy, processDataset, SelectAudioVoice, SelectAudioModel };